<template>
    <VJoyWrapper direction="column" align="stretch" row-gap="9">
        <VJoyWrapper direction="column" align="stretch" row-gap="6" no-margin>
            <section>
                <AdvancedSearchPromptContainer
                    v-model="jobDescription"
                    :enable-upload="false"
                    :show-search-cta="false"
                    :disable-text-area="!textEnabled"
                    :bottom-aligned-actions="oneEntryPointEnabled"
                    :hide-brief-details-helper="oneEntryPointEnabled"
                    :hide-prompt-feedback="oneEntryPointEnabled"
                    keyword-justify-center
                    @submit="submit"
                />
                <PromptEntityIndicatorContainer v-if="oneEntryPointEnabled" :prompt="jobDescription" />
            </section>

            <span v-if="loggedIn" class="pdf-job-description__or">
                {{ $t('search.advanced.home-v2.home.or') }}
            </span>

            <VJoyDropzone
                v-if="loggedIn"
                ref="dropzone"
                v-model="dropzoneFile"
                name="dropzone"
                :class="{'job-description-pdf-upload': pdfUploadDisabled}"
                :multiple="false"
                :accept="['application/pdf']"
                :error-text-format="dropzoneErrorFormatMessage"
                :max-size="MAX_SIZE"
                :error-text-max-size="dropzoneErrorMaxSizeMessage"
                data-testid="advanced-search-dropzone"
            >
                <template #dropzone-description>
                    {{ t('search.advanced.home-v2.pdf.dropzone.title') }}
                </template>
            </VJoyDropzone>
        </VJoyWrapper>

        <VJoyWrapper justify="center" no-margin direction="column" row-gap="6">
            <VJoyButton
                :label="t('search.advanced.ai.entrypoint.mode.ai.matching.cta.label.v2')"
                :disabled="isSubmitButtonDisabled || isSubmitButtonDebouncedDisabled"
                :loading="submitting"
                class="pdf--submit"
                data-testid="advanced-search-submit-button"
                variant="main"
                size="medium"
                @click="submit"
            />
        </VJoyWrapper>
    </VJoyWrapper>
</template>
<script setup lang="ts">
    import {computed, onMounted, ref, watch} from 'vue';
    import AdvancedSearchPromptContainer from '@search/components/prompt/AdvancedSearchPromptContainer.vue';
    import PromptEntityIndicatorContainer from '@search/components/prompt/PromptEntityIndicatorContainer.vue';
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useTranslation, navigateTo, useAuth, useLocalePrefix, useAsyncData} from '#imports';
    import {useLazyFeatureFlag} from '@malt/feature-flags';
    import {pushVJoySnackbar, VJoyWrapper, VJoyButton, VJoyDropzone} from '@maltjoy/core-vue';
    import {type ClientUserResource, type IdentityRef} from '@api';
    import {useWordCount} from '~/composables';
    import {eventBus} from './events';

    export type PdfJobDescriptionProps = {
        createAdvancedSearchFromFile: (
            file: Blob,
            onBehalfOfIdentity: string | undefined,
            referralId: string | undefined,
        ) => Promise<{
            redirectUrl: string;
        }>;
        createAdvancedSearchFromPrompt: (
            string: string,
            onBehalfOf: IdentityRef | undefined,
            referralId: string | undefined,
        ) => Promise<{
            redirectUrl: string;
        }>;
        submitting: boolean;
        context?: ClientUserResource;
        isOnBehalf: boolean;
    };
    const props = defineProps<PdfJobDescriptionProps>();

    const route = useRoute();

    const {loggedIn} = storeToRefs(useAuth());
    const oneEntryPointFF = 'one-entry-point-improvements-v2';
    const {data: oneEntryPointEnabled} = await useAsyncData(oneEntryPointFF, () => useLazyFeatureFlag(oneEntryPointFF));
    const {withLocalePrefix, withoutLocalePrefix} = useLocalePrefix();

    const MAX_SIZE = 5;
    const MIN_WORDS_COUNT_TO_ENABLE_SUBMISSION = 1;

    const {t} = useTranslation();
    const {countWords} = useWordCount();

    const dropzoneFile = ref<File[]>([]);
    const fileRef = ref<File | null>(null);
    const jobDescription = ref<string>('');
    const jobDescriptionWordsCount = computed(() => countWords(jobDescription.value));

    const referralId = ref<string | undefined>();

    const jobdescInvalid = computed(() => {
        return !fileRef.value && jobDescriptionWordsCount.value < MIN_WORDS_COUNT_TO_ENABLE_SUBMISSION;
    });
    const isSubmitButtonDisabled = computed(() => jobdescInvalid.value || props.submitting || (props.isOnBehalf && !props.context));
    const isSubmitButtonDebouncedDisabled = computed(() => jobdescInvalid.value || props.submitting || (props.isOnBehalf && !props.context));

    const pdfUploadDisabled = computed(() => jobDescription.value);
    const textEnabled = computed(() => !fileRef.value);

    const dropzoneErrorFormatMessage = t('general.errors.upload.file.format');
    const dropzoneErrorMaxSizeMessage = t('general.errors.upload.file.maxsize', {0: MAX_SIZE});

    watch(dropzoneFile, () => {
        fileRef.value = dropzoneFile.value ? dropzoneFile.value[0] : null;
    });

    async function submit() {
        if (fileRef.value) {
            await submitPdf();
        } else {
            await verifyTextBeforeSubmission();
        }
    }
    async function submitPdf() {
        try {
            const file = fileRef.value;

            if (!file) {
                pushVJoySnackbar({
                    props: {
                        // at least message is mandatory
                        message: t(
                            'search.advanced.home-v2.pdf.generation.error.file.missing',
                            'Unable to generate without a file, please upload a file first',
                        ),
                        level: 'error',
                    },
                });
                return;
            }

            eventBus.emit('submittingAndRedirecting');

            const {redirectUrl} = await props.createAdvancedSearchFromFile(file, props.context?.identityRef.identityId, referralId.value);

            await navigateAfterSubmission(redirectUrl);
        } catch {
            eventBus.emit('redirecting:abort');
        }
    }

    async function verifyTextBeforeSubmission() {
        if (!jobDescription.value) {
            pushVJoySnackbar({
                props: {
                    // at least message is mandatory
                    message: t(
                        'search.advanced.home-v2.pdf.generation.error.text.missing',
                        'Unable to generate without a text, please upload a file first',
                    ),
                    level: 'error',
                },
            });
            return;
        }

        await submitText();
    }

    async function submitText() {
        try {
            eventBus.emit('submittingAndRedirecting');

            const {redirectUrl} = await props.createAdvancedSearchFromPrompt(jobDescription.value, props.context?.identityRef, referralId.value);

            clearBusinessReferralInformation();
            await navigateAfterSubmission(redirectUrl);
        } catch {
            eventBus.emit('redirecting:abort');
        }
    }

    async function navigateAfterSubmission(redirectUrl: string) {
        const barePath = withoutLocalePrefix(redirectUrl);
        const isSearchFront = barePath === '/s' || barePath.startsWith('/s/') || barePath.startsWith('/s?');
        await navigateTo(withLocalePrefix(redirectUrl), {
            external: !isSearchFront,
        });
    }

    function clearBusinessReferralInformation() {
        localStorage.removeItem('advancedsearch.description');
        localStorage.removeItem('advancedsearch.referralId');
    }

    onMounted(() => {
        if (route.query.brief_origin === 'brp') {
            const definedDescription = localStorage.getItem('advancedsearch.description');
            const definedReferralId = localStorage.getItem('advancedsearch.referralId');

            if (definedDescription) {
                jobDescription.value = definedDescription;
            }

            if (definedReferralId) {
                referralId.value = definedReferralId;
            }
        } else {
            clearBusinessReferralInformation();
        }
    });
</script>

<style scoped lang="scss">
    .pdf-job-description {
        &__or {
            text-align: center;
            font-size: var(--joy-font-size-primary-600);
            font-weight: var(--joy-font-weight-bold);
        }
    }

    :deep(.job_description) {
        min-height: 144px;
    }

    .job_description {
        &__textarea-icon {
            display: inline-block;
            margin-right: var(--joy-core-spacing-1);
        }
    }

    .joy-textarea__custom-helpers {
        display: flex;
        margin-top: var(--joy-core-spacing-3);
        column-gap: var(--joy-core-spacing-2);
        align-items: center;

        p {
            display: flex;
            font-size: var(--joy-font-size-primary-300);
            color: var(--joy-color-text-default);
            align-items: center;
        }
    }

    .job-description-pdf-upload {
        pointer-events: none;

        :deep(.joy-dropzone__area) {
            border-color: var(--joy-color-neutral-30);
        }
    }
</style>
